// Clasificare tematica graduala: transforma un set de valori numerice in praguri (breaks).
// Modul pur (fara DOM) => aceleasi rezultate ca implementarea C# server-side (layere BBOX pe entitate),
// folosit direct client-side pentru layerele cu toate feature-urile in memorie (analize XpQuery + WFS_ALL).

export const METHOD_EQUAL = 'EQUAL';
export const METHOD_QUANTILE = 'QUANTILE';
export const METHOD_JENKS = 'JENKS';
export const METHOD_STDDEV = 'STDDEV';
export const METHOD_MANUAL = 'MANUAL';

// peste acest prag esantionam valorile pentru Jenks (O(k*n^2)) ca sa nu blocam UI-ul
const JENKS_SAMPLE_CAP = 3000;

// pastreaza doar numerele valide, sortate crescator
export function toSortedNumbers(values) {
    let result = [];

    for (let i = 0; i < values.length; i++) {
        let v = values[i];

        if (v == null || v === '') {
            continue;
        }

        let n = typeof v === 'number' ? v : Number(v);

        if (!isNaN(n) && isFinite(n)) {
            result.push(n);
        }
    }

    result.sort((a, b) => a - b);
    return result;
}

// N+1 muchii pentru N clase; intoarce null daca nu se poate clasifica
export function computeBreaks(values, method, classCount) {
    let k = Math.max(1, Math.floor(classCount || 1));
    let sorted = toSortedNumbers(values);

    if (sorted.length === 0) {
        return null;
    }

    let min = sorted[0];
    let max = sorted[sorted.length - 1];

    // toate valorile egale => o singura clasa
    if (min === max) {
        return [min, max];
    }

    switch (method) {
        case METHOD_QUANTILE: return _quantileBreaks(sorted, k);
        case METHOD_JENKS: return _jenksBreaks(sorted, k);
        case METHOD_STDDEV: return _stdDevBreaks(sorted, k);
        case METHOD_EQUAL:
        default: return _equalBreaks(min, max, k);
    }
}

// intervale de latime egala
function _equalBreaks(min, max, k) {
    let breaks = [min];
    let delta = (max - min) / k;

    for (let i = 1; i < k; i++) {
        breaks.push(min + delta * i);
    }

    breaks.push(max);
    return breaks;
}

// cuantile: acelasi numar de valori per clasa (robust la extreme)
function _quantileBreaks(sorted, k) {
    let n = sorted.length;
    let breaks = [sorted[0]];

    for (let i = 1; i < k; i++) {
        // interpolare liniara la pozitia i/k
        let pos = (i / k) * (n - 1);
        let lo = Math.floor(pos);
        let hi = Math.ceil(pos);
        let frac = pos - lo;
        breaks.push(sorted[lo] + (sorted[hi] - sorted[lo]) * frac);
    }

    breaks.push(sorted[n - 1]);
    return _dedupeMonotonic(breaks);
}

// deviatie standard: muchii la pasi de 1 sigma, simetric fata de medie, taiate la [min,max]
function _stdDevBreaks(sorted, k) {
    let n = sorted.length;
    let min = sorted[0];
    let max = sorted[n - 1];

    let sum = 0;
    for (let i = 0; i < n; i++) {
        sum += sorted[i];
    }
    let mean = sum / n;

    let sqDiff = 0;
    for (let i = 0; i < n; i++) {
        let d = sorted[i] - mean;
        sqDiff += d * d;
    }
    let sd = Math.sqrt(sqDiff / n);

    if (sd === 0) {
        return _equalBreaks(min, max, k);
    }

    let breaks = [min];
    for (let i = 0; i < k - 1; i++) {
        let edge = mean + sd * (i - (k - 2) / 2);

        if (edge > min && edge < max) {
            breaks.push(edge);
        }
    }
    breaks.push(max);

    return _dedupeMonotonic(breaks);
}

// Jenks natural breaks (Fisher): minimizeaza varianta intra-clasa
function _jenksBreaks(sorted, k) {
    let data = sorted;

    // esantionare uniforma peste prag ca sa tinem O(k*n^2) sub control
    if (data.length > JENKS_SAMPLE_CAP) {
        let sampled = [];
        let step = (data.length - 1) / (JENKS_SAMPLE_CAP - 1);

        for (let i = 0; i < JENKS_SAMPLE_CAP; i++) {
            sampled.push(data[Math.round(i * step)]);
        }
        data = sampled;
    }

    let n = data.length;

    if (k >= n) {
        return _dedupeMonotonic(data.slice());
    }

    // matrici de programare dinamica (indexate 1..n / 1..k)
    let lowerClass = [];
    let variance = [];
    for (let i = 0; i <= n; i++) {
        lowerClass.push(new Array(k + 1).fill(0));
        variance.push(new Array(k + 1).fill(0));
    }

    for (let i = 1; i <= k; i++) {
        lowerClass[1][i] = 1;
        variance[1][i] = 0;
        for (let j = 2; j <= n; j++) {
            variance[j][i] = Infinity;
        }
    }

    for (let l = 2; l <= n; l++) {
        let sum = 0;
        let sumSq = 0;
        let w = 0;
        let i4 = 0;

        for (let m = 1; m <= l; m++) {
            let lowerIdx = l - m + 1;
            let val = data[lowerIdx - 1];
            w++;
            sum += val;
            sumSq += val * val;
            let v = sumSq - (sum * sum) / w;
            i4 = lowerIdx - 1;

            if (i4 !== 0) {
                for (let j = 2; j <= k; j++) {
                    if (variance[l][j] >= v + variance[i4][j - 1]) {
                        lowerClass[l][j] = lowerIdx;
                        variance[l][j] = v + variance[i4][j - 1];
                    }
                }
            }
        }

        lowerClass[l][1] = 1;
        variance[l][1] = sumSq - (sum * sum) / w;
    }

    let breaks = new Array(k + 1);
    breaks[k] = data[n - 1];
    breaks[0] = data[0];

    let kClass = n;
    for (let j = k; j >= 2; j--) {
        let idx = lowerClass[kClass][j] - 2;
        breaks[j - 1] = data[idx];
        kClass = lowerClass[kClass][j] - 1;
    }

    return _dedupeMonotonic(breaks);
}

// paleta calitativa (Tableau10 + category10) pentru culori implicite per categorie
export const PALETTE = [
    '#4e79a7', '#f28e2b', '#e15759', '#76b7b2', '#59a14f', '#edc948', '#b07aa1', '#ff9da7', '#9c755f', '#bab0ac',
    '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd', '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf'
];

export function paletteColor(index) {
    return PALETTE[((index % PALETTE.length) + PALETTE.length) % PALETTE.length];
}

// clasificare categoriala: valori distincte + frecventa, sortate descrescator dupa frecventa
export function computeCategories(values, maxClasses) {
    let counts = new Map();

    for (let i = 0; i < values.length; i++) {
        let v = values[i];

        if (v == null || v === '') {
            continue;
        }

        let key = typeof v === 'string' ? v : String(v);
        counts.set(key, (counts.get(key) || 0) + 1);
    }

    let result = [];
    counts.forEach((count, value) => {
        result.push({ value: value, count: count });
    });

    result.sort((a, b) => b.count - a.count);

    if (maxClasses != null && maxClasses > 0 && result.length > maxClasses) {
        return { values: result.slice(0, maxClasses), truncated: true };
    }

    return { values: result, truncated: false };
}

// elimina muchii duplicate/necrescatoare pastrand N+1 monotonic strict
function _dedupeMonotonic(breaks) {
    let result = [breaks[0]];

    for (let i = 1; i < breaks.length; i++) {
        if (breaks[i] > result[result.length - 1]) {
            result.push(breaks[i]);
        }
    }

    // pastreaza mereu ultima muchie (maximul) chiar daca a fost eliminata ca duplicat
    let last = breaks[breaks.length - 1];
    if (result[result.length - 1] !== last) {
        result.push(last);
    }

    return result;
}
